import { useEffect, useRef, useState } from 'react';
import AnimatedNames from './components/AnimatedNames';
import MosaicGrid from './components/MosaicGrid';
import CountdownTimer from './components/CountdownTimer';

const FLOATING_DOTS = [
  { top: '8%', left: '3%', size: 6, delay: 0 },
  { top: '15%', left: '88%', size: 10, delay: 0.5 },
  { top: '32%', left: '95%', size: 5, delay: 1 },
  { top: '55%', left: '2%', size: 8, delay: 1.5 },
  { top: '70%', left: '91%', size: 6, delay: 0.8 },
  { top: '85%', left: '6%', size: 9, delay: 0.3 },
  { top: '90%', left: '80%', size: 5, delay: 1.2 },
  { top: '42%', left: '97%', size: 7, delay: 0.6 },
];

const COMING_SOON_TEXT = 'Coming Soon';
const BEAUTIFUL_TEXT = 'Something beautiful is being built';

function TypewriterText({ text, speed = 110, eraseSpeed = 70, pauseMs = 2800 }: {
  text: string;
  speed?: number;
  eraseSpeed?: number;
  pauseMs?: number;
}) {
  const [displayed, setDisplayed] = useState('');
  const [phase, setPhase] = useState<'typing' | 'pause' | 'erasing'>('typing');
  const indexRef = useRef(0);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;

    if (phase === 'typing') {
      if (indexRef.current < text.length) {
        timeout = setTimeout(() => {
          setDisplayed(text.slice(0, indexRef.current + 1));
          indexRef.current += 1;
        }, speed);
      } else {
        timeout = setTimeout(() => setPhase('pause'), pauseMs);
      }
    } else if (phase === 'pause') {
      timeout = setTimeout(() => setPhase('erasing'), 500);
    } else if (phase === 'erasing') {
      if (indexRef.current > 0) {
        timeout = setTimeout(() => {
          indexRef.current -= 1;
          setDisplayed(text.slice(0, indexRef.current));
        }, eraseSpeed);
      } else {
        timeout = setTimeout(() => setPhase('typing'), 600);
      }
    }

    return () => clearTimeout(timeout);
  }, [phase, displayed, text, speed, eraseSpeed, pauseMs]);

  return (
    <span className="inline-flex items-center gap-0.5">
      <span>{displayed}</span>
      <span
        className="inline-block w-px h-3 animate-pulse"
        style={{ backgroundColor: '#D4AF37', marginLeft: '1px' }}
      />
    </span>
  );
}

export default function HomePage() {
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = 'HerStory of Nigeria — Coming Soon';
  }, []);

  return (
    <div
      ref={pageRef}
      className="min-h-screen w-full relative overflow-hidden font-sans"
      style={{
        background: 'linear-gradient(145deg, #8B3A26 0%, #C1502E 35%, #A84424 65%, #6B2A18 100%)',
      }}
    >
      {/* Canvas texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='4' height='4'%3E%3Crect width='4' height='4' fill='none'/%3E%3Ccircle cx='1' cy='1' r='0.5' fill='rgba(255,248,231,0.06)'/%3E%3C/svg%3E")`,
          backgroundSize: '4px 4px',
        }}
      />

      {/* Floating gold dots */}
      {FLOATING_DOTS.map((dot, i) => (
        <div
          key={i}
          className="absolute rounded-full pointer-events-none animate-pulse_gold"
          style={{
            top: dot.top,
            left: dot.left,
            width: dot.size,
            height: dot.size,
            backgroundColor: '#D4AF37',
            opacity: 0.6,
            animationDelay: `${dot.delay}s`,
          }}
        />
      ))}

      {/* Top decorative gold line */}
      <div
        className="absolute top-0 left-0 right-0 h-1 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, #D4AF37, #F4E4C1, #D4AF37, transparent)',
        }}
      />

      {/* ─── HEADER ─── */}
      <header className="relative z-20 flex items-center justify-between px-4 sm:px-10 py-4">
        <div className="flex items-center gap-3">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_ed19985ad975b6af3d4dd22fab74d19e_5b67cba8.jpg"
            alt="HerStory of Nigeria"
            className="h-7 w-auto object-contain"
          />
        </div>
        <div
          className="text-xs font-sans tracking-[0.25em] uppercase font-medium"
          style={{ color: '#D4AF37' }}
        >
          <TypewriterText text={COMING_SOON_TEXT} speed={110} eraseSpeed={70} pauseMs={2800} />
        </div>
      </header>

      {/* ─── MAIN CONTENT ─── */}
      <main className="relative z-10 px-4 sm:px-10 pt-4 pb-10">

        {/* ─── HERO ROW ─── */}
        <div className="flex flex-col lg:flex-row gap-8 items-start mb-10">

          {/* LEFT: Big Title */}
          <div className="flex-1 pt-4 lg:pt-6 w-full">
            {/* Decorative tag above */}
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px flex-1 max-w-12" style={{ backgroundColor: 'rgba(212,175,55,0.5)' }} />
              <span
                className="text-xs font-sans tracking-[0.3em] uppercase"
                style={{ color: '#F4E4C1' }}
              >
                Living Archive
              </span>
              <div className="h-px flex-1 max-w-12" style={{ backgroundColor: 'rgba(212,175,55,0.5)' }} />
            </div>

            {/* Main title - staggered */}
            <div className="mb-2">
              <span
                className="block font-serif font-black leading-none"
                style={{
                  fontSize: 'clamp(3rem, 12vw, 6.5rem)',
                  color: '#FFF8E7',
                  letterSpacing: '-0.02em',
                }}
              >
                Her
              </span>
              <span
                className="block font-serif font-black leading-none ml-3 sm:ml-6"
                style={{
                  fontSize: 'clamp(3rem, 12vw, 6.5rem)',
                  color: '#D4AF37',
                  letterSpacing: '-0.02em',
                }}
              >
                Story
              </span>
              <div className="flex items-center gap-3 sm:gap-4">
                <div
                  className="h-0.5 w-8 sm:w-10 flex-shrink-0"
                  style={{ backgroundColor: '#D4AF37', marginLeft: '0.5rem' }}
                />
                <span
                  className="block font-serif font-light leading-none"
                  style={{
                    fontSize: 'clamp(1.6rem, 6vw, 4rem)',
                    color: 'rgba(255,248,231,0.75)',
                    letterSpacing: '0.05em',
                  }}
                >
                  of Nigeria
                </span>
              </div>
            </div>

            {/* Tagline — Women, Power & Protest only */}
            <div className="mt-6 mb-8 max-w-sm">
              <p
                className="font-sans font-light leading-relaxed"
                style={{ color: 'rgba(244,228,193,0.8)', fontSize: '0.9rem', letterSpacing: '0.03em' }}
              >
                Women, Power &amp; Protest.
              </p>
            </div>

            {/* "Something beautiful is being built" badge — ABOVE timer */}
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="relative w-2 h-2 flex items-center justify-center">
                <div
                  className="absolute w-4 h-4 rounded-full animate-ping"
                  style={{ backgroundColor: 'rgba(212,175,55,0.4)' }}
                />
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#D4AF37' }} />
              </div>
              <span
                className="font-sans text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase"
                style={{ color: '#F4E4C1' }}
              >
                <TypewriterText text={BEAUTIFUL_TEXT} speed={80} eraseSpeed={45} pauseMs={3500} />
              </span>
            </div>
          </div>

          {/* RIGHT: Animated Names Card */}
          <div
            className="relative w-full flex-shrink-0 mx-auto lg:mx-0"
            style={{ maxWidth: 'min(420px, 100%)' }}
          >
            {/* Floating accent shape behind card */}
            <div
              className="absolute -top-4 -right-4 w-full h-full rounded-lg pointer-events-none"
              style={{
                border: '1px solid rgba(212,175,55,0.2)',
                transform: 'rotate(3deg)',
              }}
            />
            <div
              className="relative rounded-lg overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, #3D1010 0%, #5C1F1F 100%)',
                border: '2px solid rgba(212,175,55,0.5)',
                minHeight: '300px',
              }}
            >
              <div
                className="absolute top-0 left-0 right-0 h-px"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.8), transparent)',
                }}
              />
              <AnimatedNames />
              <div
                className="absolute bottom-0 left-0 right-0 h-px"
                style={{
                  background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.8), transparent)',
                }}
              />
            </div>

            <p
              className="text-center mt-3 text-xs font-sans tracking-[0.2em] uppercase"
              style={{ color: 'rgba(244,228,193,0.5)' }}
            >
              IWD 2026 Celebrities
            </p>
          </div>
        </div>

        {/* ─── CENTERED COUNTDOWN ─── */}
        <div className="flex flex-col items-center mb-8">
          <CountdownTimer />
        </div>

        {/* ─── SECTION DIVIDER ─── */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="h-px flex-1" style={{ backgroundColor: 'rgba(212,175,55,0.25)' }} />
          <div className="flex gap-2 items-center">
            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: '#D4AF37', opacity: 0.6 }} />
            <span
              className="text-xs font-sans tracking-[0.2em] sm:tracking-[0.3em] uppercase whitespace-nowrap"
              style={{ color: 'rgba(244,228,193,0.5)' }}
            >
              Stories in every frame
            </span>
            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: '#D4AF37', opacity: 0.6 }} />
          </div>
          <div className="h-px flex-1" style={{ backgroundColor: 'rgba(212,175,55,0.25)' }} />
        </div>

        {/* ─── MOSAIC GRID ─── */}
        <MosaicGrid />

        {/* ─── FOOTER TAGLINE ─── */}
        <div className="mt-10 sm:mt-12 text-center px-2">
          <p
            className="font-serif italic mb-2"
            style={{ color: 'rgba(244,228,193,0.6)', fontSize: 'clamp(0.85rem, 2.5vw, 1.1rem)' }}
          >
            &ldquo;A woman&apos;s strength is like water, quiet but unstoppable.&rdquo;
          </p>
          <p
            className="font-sans text-xs tracking-widest uppercase"
            style={{ color: 'rgba(212,175,55,0.5)' }}
          >
            — NIGERIAN Proverb
          </p>

          <div className="flex flex-col items-center gap-3 mt-8">
            <div className="flex items-center gap-3">
              <div className="h-px w-12" style={{ backgroundColor: 'rgba(212,175,55,0.35)' }} />
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'rgba(212,175,55,0.6)' }} />
              <div className="h-px w-12" style={{ backgroundColor: 'rgba(212,175,55,0.35)' }} />
            </div>
            <p
              className="font-sans text-xs tracking-[0.25em] uppercase"
              style={{ color: 'rgba(244,228,193,0.35)' }}
            >
              IWD 2026 &nbsp;·&nbsp; Living Archive
            </p>
            <a
              href="https://herstoryofnigeria.com"
              className="text-xs font-sans tracking-[0.2em] sm:tracking-[0.3em] uppercase cursor-pointer transition-colors duration-300"
              style={{ color: 'rgba(244,228,193,0.35)' }}
              onMouseEnter={(e) => { (e.target as HTMLAnchorElement).style.color = '#D4AF37'; }}
              onMouseLeave={(e) => { (e.target as HTMLAnchorElement).style.color = 'rgba(244,228,193,0.35)'; }}
              rel="nofollow"
            >
              herstoryofnigeria.com
            </a>
          </div>
        </div>
      </main>

      {/* Bottom gold line */}
      <div
        className="absolute bottom-0 left-0 right-0 h-1 pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, #D4AF37, #F4E4C1, #D4AF37, transparent)',
        }}
      />
    </div>
  );
}
